// src/pages/LoginPage.js
import React, { useEffect, useMemo } from 'react';
import { Navigate, useLocation, Link, useNavigate } from 'react-router-dom';
import { Authenticator, ThemeProvider } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import './LoginPage.css';

/**
 * Create theme based on current mode (dark/light)
 */
const createAuthTheme = (isDark) => ({
  name: 'emerging-robotics-theme',
  tokens: {
    colors: {
      background: {
        primary: { value: isDark ? '#0a0a0a' : '#f5f5f5' },
        secondary: { value: isDark ? '#111111' : '#ffffff' },
        tertiary: { value: isDark ? '#1a1a1a' : '#fafafa' },
      },
      font: {
        primary: { value: isDark ? '#fafafa' : '#1a1a1a' },
        secondary: { value: isDark ? '#a1a1a1' : '#4a4a4a' },
        interactive: { value: '#ff6b35' },
      },
      brand: {
        primary: {
          10: { value: isDark ? '#1a0d08' : '#fff5f0' },
          20: { value: isDark ? '#331a10' : '#ffe8db' },
          40: { value: isDark ? '#662d18' : '#ffc9a8' },
          60: { value: '#cc4d22' },
          80: { value: '#ff6b35' },
          90: { value: '#ff8c5a' },
          100: { value: '#ffb899' },
        },
      },
      border: {
        primary: { value: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)' },
        secondary: { value: 'rgba(255, 107, 53, 0.3)' },
        focus: { value: '#ff6b35' },
      },
    },
    components: {
      authenticator: {
        router: {
          boxShadow: { value: isDark ? '0 4px 40px rgba(0, 0, 0, 0.5)' : '0 4px 40px rgba(0, 0, 0, 0.15)' },
          borderWidth: { value: '1px' },
          borderColor: { value: 'rgba(255, 107, 53, 0.2)' },
          backgroundColor: { value: isDark ? 'rgba(20, 20, 20, 0.95)' : 'rgba(255, 255, 255, 0.95)' },
        },
      },
      button: {
        primary: {
          backgroundColor: { value: '#ff6b35' },
          color: { value: '#0a0a0a' },
          borderColor: { value: 'transparent' },
          _hover: {
            backgroundColor: { value: '#f7931e' },
            borderColor: { value: 'transparent' },
          },
          _focus: {
            backgroundColor: { value: '#f7931e' },
            borderColor: { value: '#ff6b35' },
          },
          _active: {
            backgroundColor: { value: '#e65c26' },
          },
        },
        link: {
          color: { value: '#ff6b35' },
          _hover: {
            color: { value: '#f7931e' },
            backgroundColor: { value: 'transparent' },
          },
        },
      },
      fieldcontrol: {
        backgroundColor: { value: isDark ? 'rgba(26, 26, 26, 0.8)' : 'rgba(255, 255, 255, 0.8)' },
        borderColor: { value: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.15)' },
        color: { value: isDark ? '#fafafa' : '#1a1a1a' },
        _focus: {
          borderColor: { value: '#ff6b35' },
          boxShadow: { value: '0 0 0 2px rgba(255, 107, 53, 0.2)' },
        },
      },
      tabs: {
        item: {
          color: { value: isDark ? '#a1a1a1' : '#4a4a4a' },
          borderColor: { value: 'transparent' },
          _hover: {
            color: { value: '#ff6b35' },
          },
          _active: {
            color: { value: '#ff6b35' },
            borderColor: { value: '#ff6b35' },
          },
        },
      },
    },
    radii: {
      small: { value: '4px' },
      medium: { value: '8px' },
      large: { value: '16px' },
    },
    space: {
      small: { value: '0.75rem' },
      medium: { value: '1rem' },
      large: { value: '1.5rem' },
    },
    fontSizes: {
      small: { value: '0.875rem' },
      medium: { value: '1rem' },
      large: { value: '1.125rem' },
    },
  },
});

/**
 * Custom components for the Authenticator
 */
const authComponents = {
  Header() {
    return (
      <div className="auth-header">
        <div className="auth-logo">
          <img src="/er-logo.svg" alt="ER" className="auth-logo-img" />
        </div>
      </div>
    );
  },
  Footer() {
    return (
      <div className="auth-footer">
        <p>Access is by invitation only.</p>
        <Link to="/" className="auth-back-link">
          Back to Home
        </Link>
      </div>
    );
  },
  SignIn: {
    Header() {
      return <h2 className="auth-title">Sign In</h2>;
    },
  },
  SignUp: {
    Header() {
      return <h2 className="auth-title">Create Account</h2>;
    },
  },
  ConfirmSignUp: {
    Header() {
      return <h2 className="auth-title">Verify Email</h2>;
    },
  },
  ForgotPassword: {
    Header() {
      return <h2 className="auth-title">Reset Password</h2>;
    },
  },
  ConfirmResetPassword: {
    Header() {
      return <h2 className="auth-title">New Password</h2>;
    },
  },
};

/**
 * Login Page with embedded Authenticator
 */
const LoginPage = () => {
  const { isAuthenticated, authConfigured, checkAuth } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  // Memoize the auth theme based on current theme
  const authTheme = useMemo(() => createAuthTheme(theme === 'dark'), [theme]);

  // Get the page user was trying to access
  const from = location.state?.from || '/';

  // When user successfully authenticates, refresh context and redirect
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, from, navigate]);

  // Auth not configured - show configuration message
  if (!authConfigured) {
    return (
      <div className="login-page">
        <div className="page-bg">
          <div className="bg-gradient" />
          <div className="grid-pattern" />
        </div>

        <section className="login-section">
          <div className="section-container">
            <div className="login-card">
              <div className="login-icon warning">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>

              <h1>Authentication Not Configured</h1>
              <p className="login-description">
                AWS Cognito authentication has not been set up yet.
                Please configure the environment variables to enable authentication.
              </p>

              <div className="config-instructions">
                <p>To configure authentication:</p>
                <ol>
                  <li>Copy <code>.env.example</code> to <code>.env</code></li>
                  <li>Fill in your AWS Cognito credentials</li>
                  <li>Restart the development server</li>
                </ol>
              </div>

              <Link to="/" className="login-back">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Already authenticated - redirect
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="login-page">
      {/* Background */}
      <div className="page-bg">
        <div className="bg-gradient" />
        <div className="grid-pattern" />
      </div>

      <section className="login-section">
        <div className="section-container authenticator-container">
          <ThemeProvider theme={authTheme}>
            <Authenticator
              components={authComponents}
              hideSignUp={true}
              variation="default"
            >
              {({ signOut, user }) => {
                // User signed in - trigger context update and redirect
                if (user) {
                  checkAuth();
                }
                return null;
              }}
            </Authenticator>
          </ThemeProvider>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
